import React from 'react';
import { motion } from 'framer-motion';
interface GeoGridProps {
  width: number;
  height: number;
  depth?: number;
  mode?: 'stimulus' | 'support' | 'perimeter' | 'area';
  label?: string;
  highlighted?: number;
  showGrid?: boolean;
  unit?: string;
}
export function GeoGrid({
  width,
  height,
  depth,
  mode = 'area',
  label,
  highlighted,
  showGrid = true,
  unit
}: GeoGridProps) {
  const padding = 40;
  // Dynamic cell size: scale down for large dimensions to fit max ~500px
  const maxSvgDim = 460; // max pixel dimension for the grid area
  const rawCellSize = 20;
  const maxCellsByWidth = maxSvgDim / width;
  const maxCellsByHeight = maxSvgDim / height;
  const cellSize = Math.min(rawCellSize, maxCellsByWidth, maxCellsByHeight);
  // For very large grids (>20 cells), disable individual cell rendering and show a solid rect
  const tooManyGridCells = width * height > 400;
  const effectiveShowGrid = showGrid && !tooManyGridCells;
  // Determine display mode
  const isStimulus = mode === 'stimulus';
  const isPerimeter = mode === 'perimeter';
  const isArea = mode === 'area' || mode === 'support';
  const showCalculation = !isStimulus;
  const unitLabel = unit ? ` ${unit}` : '';
  const svgWidth = Math.round(width * cellSize + padding * 2);
  const svgHeight = Math.round(height * cellSize + padding * 2);
  return <div className="bg-slate-50 p-6 rounded-xl border border-slate-100" data-id="element-7667">
      {label && <div className="text-sm font-medium text-slate-700 mb-3 text-center" data-id="element-7668">
          {label}
        </div>}
      <div className="flex justify-center overflow-hidden" data-id="element-7669">
        <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="max-w-full h-auto" data-id="element-7670">
          {/* Grid cells */}
          {effectiveShowGrid && Array.from({
          length: height
        }).map((_, row) => Array.from({
          length: width
        }).map((_, col) => <motion.rect key={`${row}-${col}`} x={padding + col * cellSize} y={padding + row * cellSize} width={cellSize} height={cellSize} fill={isPerimeter ? 'white' : 'rgb(199, 210, 254)'} stroke="rgb(148, 163, 184)" strokeWidth="1" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: Math.min((row * width + col) * 0.02, 0.5)
        }} data-id="element-7671" />))}

          {/* Solid rectangle (when grid is off or too many cells) */}
          {(!showGrid || tooManyGridCells) && <motion.rect x={padding} y={padding} width={width * cellSize} height={height * cellSize} fill="rgb(199, 210, 254)" stroke="rgb(148, 163, 184)" strokeWidth="2" rx="4" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} data-id="element-7672" />}

          {/* Perimeter highlight */}
          {isPerimeter && <motion.rect x={padding} y={padding} width={width * cellSize} height={height * cellSize} fill="none" stroke="rgb(99, 102, 241)" strokeWidth="4" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 1.5,
          ease: 'easeInOut'
        }} data-id="element-7673" />}

          {/* Width dimension (top) */}
          <text x={padding + width * cellSize / 2} y={padding - 10} textAnchor="middle" className="text-sm font-bold fill-slate-700" data-id="element-7674">
            {width}
            {unitLabel}
          </text>

          {/* Height dimension (left) */}
          <text x={padding - 10} y={padding + height * cellSize / 2} textAnchor="end" dominantBaseline="middle" className="text-sm font-bold fill-slate-700" data-id="element-7675">
            {height}
            {unitLabel}
          </text>

          {/* Depth dimension */}
          {depth && <text x={padding + width * cellSize + 10} y={padding + height * cellSize + 15} textAnchor="start" className="text-sm font-bold fill-indigo-600" data-id="element-7676">
              diepte: {depth}
              {unitLabel}
            </text>}
        </svg>
      </div>

      {/* Calculation text */}
      {showCalculation && <div className="mt-4 text-center text-sm text-slate-600" data-id="element-7677">
          {depth ? <p data-id="element-7678">
              Inhoud = {width} × {depth} × {height} = {width * depth * height}
              {unit ? ` ${unit}³` : ''}
            </p> : isPerimeter ? <p data-id="element-7679">
              Omtrek = {width} + {height} + {width} + {height} ={' '}
              {2 * (width + height)}
              {unitLabel && ` ${unit}`}
            </p> : <p data-id="element-7680">
              Oppervlakte = {width} × {height} = {width * height}
              {unitLabel && ` ${unit}²`}
            </p>}
        </div>}
    </div>;
}